/**
 * `heysegment push` (and `save`) — validate, check safe-state, then pack and
 * upload the project to the session's version.
 *
 * Order: validate (theme + fonts + build) → safe-state check → pack → getPushUrl
 * → PUT → report `pushed` (bumps the version revision). The push aborts before
 * packing if the version is contended (someone else pushed since you pulled, or
 * another session is editing it) unless `--acknowledge` is passed. `--skip-validate`
 * bypasses validation; `save` is the everyday alias.
 *
 * `--json` makes push machine-readable for the VTM: on validation failure it
 * prints the structured validation report (theme/font/build detail + `aiText`)
 * to stdout and exits 1; on full success it prints the same report (ok=true) and
 * exits 0. Nothing else is written to stdout in `--json` mode, so a contended
 * block / transfer error leaves stdout empty — the consumer reads that as "could
 * not push" rather than "validation failed". The shape is the `validate --json`
 * envelope, so a single decoder handles both commands.
 *
 * @module
 */
import { Console, Effect, FileSystem } from "effect"
import { Command, Flag } from "effect/unstable/cli"
import { FetchHttpClient } from "effect/unstable/http"
import { withSession, step, type CliSession } from "../session/with-session"
import { OperationFailed } from "../errors"
import * as archive from "../session/archive"
import * as transfer from "../session/transfer"
import { buildAt, fontsAt, themeAt } from "./validate"
import { emitReports, toJsonPayload } from "../output"
import { SessionClient } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

const skipValidateFlag = Flag.boolean("skip-validate").pipe(
  Flag.withDescription("Skip the pre-push validation (theme + fonts + build)"),
)

const acknowledgeFlag = Flag.boolean("acknowledge").pipe(
  Flag.withDescription(
    "Acknowledge and override a contended version (overwrites concurrent changes)",
  ),
)

const dryRunFlag = Flag.boolean("dry-run").pipe(
  Flag.withDescription("Run validation and safety checks without packing or uploading"),
)

const jsonFlag = Flag.boolean("json").pipe(
  Flag.withDescription(
    "Emit the structured validation report (theme + fonts + build) instead of human output",
  ),
)

const pushProgram = (input: {
  readonly session: CliSession
  readonly skipValidate: boolean
  readonly acknowledge: boolean
  readonly dryRun: boolean
  readonly json: boolean
}) =>
  Effect.scoped(
    Effect.gen(function* () {
      const client = yield* SessionClient

      // 1. Validate (unless skipped). The reports feed the human output and the
      //    `--json` success payload; on failure we abort before any network.
      const reports = input.skipValidate
        ? []
        : yield* Effect.all([
            themeAt(input.session.projectDir),
            fontsAt(input.session.projectDir),
            buildAt(input.session.projectDir),
          ])

      if (!input.skipValidate) {
        const failed = reports.filter((r) => !r.ok)
        if (input.json) {
          // Emit the report only on failure here; on success we emit after upload
          // so a later transfer error never looks like a successful push.
          if (failed.length > 0) {
            yield* Console.log(JSON.stringify(toJsonPayload(reports), null, 2))
            return yield* Effect.fail(new OperationFailed({ failed: failed.map((r) => r.check) }))
          }
        } else {
          yield* Console.log("Validating before push…")
          yield* emitReports(reports, { json: false }).pipe(
            Effect.tapError(() => Console.error("\nValidation failed — not pushing.")),
          )
        }
      }

      // 2. Acknowledge an override up front if asked.
      if (input.acknowledge) {
        yield* step("push:acknowledge", client.acknowledgeOverride(void 0))
      }

      // 3. Safe-state gate — don't even pack if the version is contended.
      const safety = yield* step("push:check", client.getSafetyState(void 0))
      if (!safety.safeToPush) {
        yield* Console.error(`\nPush blocked — ${safety.reasons.join("; ")}.`)
        yield* Console.error(
          "Run `heysegment pull` to take the latest, or `heysegment push --acknowledge` to override.",
        )
        return yield* Effect.fail(new OperationFailed({ failed: ["push:contended"] }))
      }

      if (input.dryRun) {
        if (input.json) {
          yield* Console.log(
            JSON.stringify(
              {
                ok: true,
                dryRun: true,
                versionId: input.session.versionId,
                excluded: archive.ARCHIVE_EXCLUDES,
              },
              null,
              2,
            ),
          )
        } else {
          yield* Console.log(
            `✓ Dry run passed for version ${input.session.versionId}; no files were uploaded.`,
          )
          yield* Console.log(
            `  Push archive excludes: ${archive.ARCHIVE_EXCLUDES.join(", ")}`,
          )
        }
        return
      }

      // 4. Pack → upload → report.
      const fs = yield* FileSystem.FileSystem
      const tmp = yield* step("push:tmp", fs.makeTempFileScoped({ suffix: ".tar.gz" }))
      yield* step("push:pack", archive.pack({ projectDir: input.session.projectDir, out: tmp }))
      const presigned = yield* step("push:url", client.getPushUrl(void 0))
      yield* step(
        "push:upload",
        transfer
          .upload({ url: presigned.url, fromFile: tmp, contentType: "application/gzip" })
          .pipe(Effect.provide(FetchHttpClient.layer)),
      )
      // Records the push and bumps the version revision server-side.
      yield* step("push:report", client.report({ type: "pushed" }))

      // 5. Terminal output.
      if (input.json) {
        yield* Console.log(JSON.stringify(toJsonPayload(reports), null, 2))
      } else {
        yield* Console.log(`✓ Pushed version ${input.session.versionId}`)
      }
    }),
  )

export const pushCommand = Command.make(
  "push",
  {
    dir: dirFlag,
    apiUrl: apiUrlFlag,
    skipValidate: skipValidateFlag,
    acknowledge: acknowledgeFlag,
    dryRun: dryRunFlag,
    json: jsonFlag,
  },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, (session) =>
      pushProgram({
        session,
        skipValidate: flags.skipValidate,
        acknowledge: flags.acknowledge,
        dryRun: flags.dryRun,
        json: flags.json,
      }),
    ),
).pipe(
  Command.withDescription(
    "Validate, check safe-state, then pack and upload to the session's version",
  ),
)

export const saveCommand = Command.make(
  "save",
  {
    dir: dirFlag,
    apiUrl: apiUrlFlag,
    skipValidate: skipValidateFlag,
    acknowledge: acknowledgeFlag,
    dryRun: dryRunFlag,
    json: jsonFlag,
  },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, (session) =>
      pushProgram({
        session,
        skipValidate: flags.skipValidate,
        acknowledge: flags.acknowledge,
        dryRun: flags.dryRun,
        json: flags.json,
      }),
    ),
).pipe(
  Command.withDescription("Save your work — validate, check, pack, and upload (alias for push)"),
)
